import 'react-big-calendar/lib/css/react-big-calendar.css';

import moment from 'moment'; // For date formatting
import React from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';

import { Timestamp } from '@firebase/firestore';

import { useModuleTranslation } from '../lang';
import { Batch } from '../models/Batch';
import { toDate } from '../utils/timestamp';

interface CalendarWidgetProps {
  batches: Batch[];
}

interface Event {
  title: string;
  start: Date;
  end: Date;
  description: string;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ batches }) => {
  const localizer = momentLocalizer(moment);
  const t = useModuleTranslation('CalendarWidget');

  // Function to calculate the end date of a step based on its duration
  const calculateStepEndDate = (startDate: Date, duration: number): Date => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration);
    return endDate;
  };

  // Function to set a fixed 30-minute duration for the calendar event view
  const set30MinuteDuration = (startDate: Date): Date => {
    const endDate = new Date(startDate);
    endDate.setMinutes(startDate.getMinutes() + 30);
    return endDate;
  };

  // Prepare events for the calendar from batch steps
  const events: Event[] = batches.flatMap((batch) => {
    const recipeSteps = batch.recipe?.steps || [];

    let previousStepEndDate: Date | null = batch.startDate instanceof Timestamp ? batch.startDate.toDate() : null;

    const stepEvents =
      recipeSteps.length > 0
        ? recipeSteps
            .map((step) => {
              let stepStartDate = step?.start instanceof Timestamp ? step.start.toDate() : previousStepEndDate;

              if (
                previousStepEndDate &&
                stepStartDate &&
                stepStartDate?.toDateString() === previousStepEndDate?.toDateString()
              ) {
                stepStartDate = new Date(previousStepEndDate.getTime());
                stepStartDate.setMinutes(stepStartDate.getMinutes() + 30);
              }

              const stepEndDate =
                step?.end instanceof Timestamp
                  ? step.end.toDate()
                  : stepStartDate
                    ? calculateStepEndDate(stepStartDate, step.duration)
                    : null;

              if (!stepStartDate) return null;

              const calendarEndDate = set30MinuteDuration(stepStartDate);
              previousStepEndDate = stepEndDate || stepStartDate;

              return {
                title: `${batch.name}: ${step.name}`,
                start: stepStartDate,
                end: calendarEndDate,
                description: step.description || t.noDescription,
              } as Event;
            })
            .filter((event): event is Event => event !== null)
        : [];

    // Add a fallback "Start {batch.name}" event if no steps exist but there's a startDate
    if (stepEvents.length === 0 && !!batch.startDate) {
      const start = toDate(batch.startDate);
      return [
        {
          title: `${t.start} ${batch.name}`,
          start,
          end: set30MinuteDuration(start),
          description: t.plannedStart,
        } as Event,
      ];
    }

    return stepEvents;
  });

  return (
    <div style={{ height: 600 }}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor={(event: Event) => event.start} // Access start date
        endAccessor={(event: Event) => event.end} // Access end date (fixed 30 minutes)
        titleAccessor={(event: Event) => event.title} // Access event title
        style={{ height: '100%' }}
        defaultView="month"
        views={['month']}
      />
    </div>
  );
};

export default CalendarWidget;
